/**
 * registry.ts — the InsertTarget registry for the floating AskBox (Cmd/Ctrl+K).
 *
 * The AskBox needs to (a) capture the active editor's selection before opening
 * and (b) insert the response at the cursor when it returns. Different surfaces
 * (Monaco, dashboard inputs) register an InsertTarget on focus and unregister on
 * blur/unmount. The box uses the most-recently-focused target.
 *
 * This is the extension seam the plan calls out: Phase 1 ships Monaco-only, but
 * a dashboard input registers the same way (`registerInsertTarget(...)` on
 * focus) and the box picks it up with no AskBox change.
 *
 * The registry is a module-level singleton (one per app). A target's
 * `getSelection()` returns the current text selection + cursor rectangle (for
 * positioning the floating box near the cursor). `insert(text)` replaces the
 * selection (or inserts at the cursor). `kind` is for telemetry/UX hints.
 */
export interface CursorRect {
  left: number;
  top: number;
  bottom: number;
}

export interface InsertSelection {
  text: string;
  /** File/path context the box can send to the backend (optional). */
  file?: string;
  /** [startLine, endLine] (1-based) — Monaco selections carry these so the
   *  agent context can cite "src/economy.py lines 12–30". */
  lines?: [number, number];
}

export interface InsertTarget {
  /** Stable id for the target (e.g. `monaco:src/economy.py`). */
  id: string;
  /** What kind of surface (drives placeholder text + hints). */
  kind: 'monaco' | 'input' | 'textarea';
  /** Current text selection + the file path, if known. Null when nothing selected. */
  getSelection: () => InsertSelection | null;
  /** Insert/replace text at the current selection. */
  insert: (text: string) => void;
  /** The cursor's screen rectangle, for positioning the floating box. */
  getRect: () => CursorRect | null;
}

interface RegisteredTarget {
  target: InsertTarget;
  at: number; // monotonic registration order — highest wins (most-recent)
}

const targets = new Map<string, RegisteredTarget>();
let _counter = 0;

/** Register an insert target. Returns an unregister function. */
export function registerInsertTarget(target: InsertTarget): () => void {
  // A monotonic counter (not Date.now()) breaks ties deterministically when
  // multiple targets register in the same millisecond.
  _counter += 1;
  targets.set(target.id, { target, at: _counter });
  return () => {
    // Only delete if it's still ours (a re-register with the same id replaces).
    const existing = targets.get(target.id);
    if (existing && existing.target === target) {
      targets.delete(target.id);
    }
  };
}

/** The most-recently-registered active target, or null when none. */
export function activeInsertTarget(): InsertTarget | null {
  let best: RegisteredTarget | null = null;
  for (const entry of targets.values()) {
    if (best === null || entry.at > best.at) best = entry;
  }
  return best?.target ?? null;
}

/** Test hook: clear all targets. */
export function _clearTargetsForTest(): void {
  targets.clear();
  _counter = 0;
}
